import { t, useLanguage } from "@/lib/i18n";
import React, { useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import GoogleSignIn from "@/components/GoogleSignIn";

export default function VerifyEmail() {
  useLanguage();
  const { user, ready, updateUser, logout } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token");
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const perform = async (action) => {
    setBusy(true); setMessage(""); setError("");
    try { await action(); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || "Please try again."); }
    finally { setBusy(false); }
  };
  const refresh = async () => {
    const { data } = await api.get("/auth/me");
    updateUser(data);
    if (data.email_verified) navigate("/app", { replace: true });
    else setMessage("Your email is not verified yet. Open the link in your inbox.");
  };
  const request = (event) => {
    event.preventDefault();
    perform(async () => {
      const { data } = await api.post("/auth/verification/request", user.email ? {} : { email });
      setMessage(data.message);
      updateUser((await api.get("/auth/me")).data);
    });
  };
  const confirm = () => perform(async () => {
    const { data } = await api.post("/auth/verification/confirm", { token });
    updateUser(data);
    navigate("/app", { replace: true });
  });
  return <main className="min-h-screen flex items-center justify-center bg-[#FAF7F2] p-5">
    <section className="w-full max-w-md bg-white border rounded-xl p-6 space-y-5">
      <h1 className="font-serif text-3xl">{t("Verify your email")}</h1>
      {!ready ? <p>{t("Loading your account...")}</p> : !user || user.role === "guest" ? <>
        <p>{t("Sign in to the account that requested this verification link.")}</p>
        <Link className="text-amber-800 underline" to="/login" state={{ returnTo: location.pathname + location.search }}>{t("Sign in")}</Link>
      </> : user.email_verified ? <>
        <p>{t("Your email is verified.")}</p><Link className="text-amber-800 underline" to="/app">{t("Continue to your tree")}</Link>
      </> : <>
        <p className="text-stone-600">{t("Verify {email} to access your account and add family members.", { email: user.email || t("your email address") })}</p>
        {!user.google_connected && <GoogleSignIn link onSuccess={(data) => {
          updateUser(data.user);
          if (data.user.email_verified) navigate("/app", { replace: true });
          else setMessage("Google connected. Request a verification email below to verify this email address.");
        }} />}
        {message && <p role="status" className="text-sm text-green-800">{t(message)}</p>}
        {error && <p role="alert" className="text-sm text-red-700">{t(error)}</p>}
        {token && <Button disabled={busy} onClick={confirm} className="w-full">{busy ? t("Please wait...") : t("Confirm email verification")}</Button>}
        <form onSubmit={request} className="space-y-3">
          {!user.email && <div className="space-y-2"><Label htmlFor="verification-email">{t("Email address")}</Label>
            <Input id="verification-email" autoComplete="email" type="email" required value={email} onChange={(event) => setEmail(event.target.value)} /></div>}
          <Button type="submit" variant="outline" disabled={busy} className="w-full">{t("Send verification email")}</Button>
        </form>
        <p className="text-xs text-stone-500">{t("Check your inbox and spam folder. Links expire in 30 minutes. Wait 60 seconds before requesting another link.")}</p>
        <Button variant="outline" disabled={busy} onClick={() => perform(refresh)} className="w-full">{t("I've verified my email")}</Button>
      </>}
      {user && user.role !== "guest" && <Button variant="ghost" disabled={busy} onClick={async () => { await logout(); navigate("/login"); }}>{t("Sign out")}</Button>}
    </section>
  </main>;
}
